import React from 'react';
import {
  cancelScheduledMessage,
  deleteScheduledMessage,
  forceDispatchScheduledMessage,
  resendScheduledMessage,
  retryScheduledMessage,
  sendScheduledMessageTemplate,
} from '../../../api/scheduledMessages';
import {
  cancelScheduledMessageAdmin,
  deleteScheduledMessageAdmin,
  forceDispatchScheduledMessageAdmin,
  resendScheduledMessageAdmin,
  retryScheduledMessageAdmin,
  sendScheduledMessageTemplateAdmin,
} from '../../../api/scheduledMessagesAdmin';
import { getApiErrorMessage } from '../../../utils/errors';
import ScheduledMessageActionsMenu from './ScheduledMessageActionsMenu';
import ScheduledMessageAgendaCell from './ScheduledMessageAgendaCell';
import {
  GenerationStatusBadge,
  ScheduleStatusBadge,
} from './StatusBadge';
import {
  buildScheduledMessagesEditPath,
  buildScheduledMessagesPath,
  buildUserStatusHint,
  contactLabel,
} from '../utils';
import { buildScheduledMessageLoadingState } from '../scheduledMessageLoadingState';

function ScheduledMessageAgentCell({ item }) {
  const accountTitle = [item.account?.name, item.account?.email].filter(Boolean).join(' · ');

  return (
    <div className="min-w-0 space-y-0.5">
      <p className="truncate text-content-secondary" title={item.agent?.name || undefined}>
        {item.agent?.name || '—'}
      </p>
      <p className="truncate text-xs text-content-muted" title={accountTitle || undefined}>
        {item.account?.name || '—'}
      </p>
    </div>
  );
}

function ScheduledMessageStatusCell({ item }) {
  const statusHint = buildUserStatusHint(item);

  return (
    <div
      className="flex flex-col gap-1 items-start min-w-0"
      title={statusHint || undefined}
    >
      <ScheduleStatusBadge status={item.status} compact title={statusHint} />
      {item.content_generation_mode === 'batch' && (
        <GenerationStatusBadge status={item.generation_status} compact />
      )}
    </div>
  );
}

function ScheduledMessagesTable({
  mode,
  items,
  agentId,
  contactsById,
  showAgentColumn,
  onCancelSuccess,
  onCancelError,
  onDeleteSuccess,
  onDeleteError,
  onRetrySuccess,
  onRetryError,
  onResendSuccess,
  onResendError,
  onForceDispatchSuccess,
  onForceDispatchError,
  onSendTemplateSuccess,
  onSendTemplateError,
}) {
  const [cancellingId, setCancellingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [retryingId, setRetryingId] = React.useState(null);
  const [resendingId, setResendingId] = React.useState(null);
  const [dispatchingId, setDispatchingId] = React.useState(null);
  const [sendingTemplateId, setSendingTemplateId] = React.useState(null);

  const handleCancel = async (item) => {
    if (!window.confirm('Cancelar este agendamento?')) {
      return;
    }

    setCancellingId(item.id);
    try {
      if (mode === 'admin') {
        await cancelScheduledMessageAdmin(item.id);
      } else {
        await cancelScheduledMessage(item.id);
      }
      onCancelSuccess?.();
    } catch (err) {
      onCancelError?.(getApiErrorMessage(err, 'Não foi possível cancelar o agendamento.'));
    } finally {
      setCancellingId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Excluir este agendamento permanentemente?')) {
      return;
    }

    setDeletingId(item.id);
    try {
      if (mode === 'admin') {
        await deleteScheduledMessageAdmin(item.id);
      } else {
        await deleteScheduledMessage(item.id);
      }
      onDeleteSuccess?.();
    } catch (err) {
      onDeleteError?.(getApiErrorMessage(err, 'Não foi possível excluir o agendamento.'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleRetry = async (item) => {
    if (!window.confirm('Este agendamento está preso em processamento. Reenfileirar envio?')) {
      return;
    }

    setRetryingId(item.id);
    try {
      if (mode === 'admin') {
        await retryScheduledMessageAdmin(item.id);
      } else {
        await retryScheduledMessage(item.id);
      }
      onRetrySuccess?.();
    } catch (err) {
      onRetryError?.(getApiErrorMessage(err, 'Não foi possível reprocessar o agendamento.'));
    } finally {
      setRetryingId(null);
    }
  };

  const handleResend = async (item) => {
    if (!window.confirm('Reenviar a mensagem gerada para o contato?')) {
      return;
    }

    setResendingId(item.id);
    try {
      if (mode === 'admin') {
        await resendScheduledMessageAdmin(item.id);
      } else {
        await resendScheduledMessage(item.id);
      }
      onResendSuccess?.();
    } catch (err) {
      onResendError?.(getApiErrorMessage(err, 'Não foi possível reenviar a mensagem.'));
    } finally {
      setResendingId(null);
    }
  };

  const handleForceDispatch = async (item) => {
    if (
      !window.confirm(
        'Gerar e enviar a mensagem agora? O horário agendado será ignorado. Isso pode levar alguns minutos.'
      )
    ) {
      return;
    }

    setDispatchingId(item.id);
    try {
      if (mode === 'admin') {
        await forceDispatchScheduledMessageAdmin(item.id);
      } else {
        await forceDispatchScheduledMessage(item.id);
      }
      onForceDispatchSuccess?.();
    } catch (err) {
      onForceDispatchError?.(
        getApiErrorMessage(err, 'Não foi possível enviar o agendamento agora.')
      );
    } finally {
      setDispatchingId(null);
    }
  };

  const handleSendTemplate = async (item) => {
    if (
      !window.confirm(
        'Enviar apenas o template WhatsApp? O conteúdo completo será entregue após o contato interagir.'
      )
    ) {
      return;
    }

    setSendingTemplateId(item.id);
    try {
      if (mode === 'admin') {
        await sendScheduledMessageTemplateAdmin(item.id);
      } else {
        await sendScheduledMessageTemplate(item.id);
      }
      onSendTemplateSuccess?.();
    } catch (err) {
      onSendTemplateError?.(
        getApiErrorMessage(err, 'Não foi possível enviar o template.')
      );
    } finally {
      setSendingTemplateId(null);
    }
  };

  const detailPath = (id) => buildScheduledMessagesPath(mode, { agentId, scheduledMessageId: id });
  const editPath = (item) =>
    buildScheduledMessagesEditPath(mode, {
      agentId: agentId || item.agent_id,
      scheduledMessageId: item.id,
    });

  const actionMenuProps = (item) => ({
    item,
    detailTo: detailPath(item.id),
    editTo: editPath(item),
    loadingState: buildScheduledMessageLoadingState({
      itemId: item.id,
      cancellingId,
      deletingId,
      retryingId,
      resendingId,
      dispatchingId,
      sendingTemplateId,
    }),
    onCancel: handleCancel,
    onDelete: handleDelete,
    onRetry: handleRetry,
    onResend: handleResend,
    onForceDispatch: handleForceDispatch,
    onSendTemplate: handleSendTemplate,
  });

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm table-auto">
          <thead>
            <tr className="border-b border-surface-border text-left">
              <th className="px-1 py-2.5 font-medium text-content-muted whitespace-nowrap">Contato</th>
              {showAgentColumn && (
                <th className="px-3 py-2.5 font-medium text-content-muted whitespace-nowrap">Agente</th>
              )}
              <th className="px-3 py-2.5 font-medium text-content-muted">Agenda</th>
              <th className="px-3 py-2.5 font-medium text-content-muted whitespace-nowrap">Status</th>
              <th className="px-1 py-2.5 font-medium text-content-muted text-right w-12">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {items.map((item) => {
              const contact = contactLabel(item, contactsById);

              return (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50/60 transition-colors"
                >
                  <td className="px-1 py-3 font-medium text-content-primary min-w-[9rem]">
                    <p className="truncate" title={contact}>
                      {contact}
                    </p>
                  </td>
                  {showAgentColumn && (
                    <td className="px-3 py-3 min-w-[10rem]">
                      <ScheduledMessageAgentCell item={item} />
                    </td>
                  )}
                  <td className="px-3 py-3 min-w-[14rem]">
                    <ScheduledMessageAgendaCell item={item} />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <ScheduledMessageStatusCell item={item} />
                  </td>
                  <td className="px-1 py-3 text-right">
                    <ScheduledMessageActionsMenu {...actionMenuProps(item)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="rounded-xl border border-surface-border bg-white px-4 py-3 space-y-2.5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-content-primary truncate">
                  {contactLabel(item, contactsById)}
                </p>
                {showAgentColumn && (
                  <ScheduledMessageAgentCell item={item} />
                )}
              </div>
              <ScheduledMessageActionsMenu {...actionMenuProps(item)} />
            </div>
            <ScheduledMessageAgendaCell item={item} />
            <ScheduledMessageStatusCell item={item} />
          </div>
        ))}
      </div>
    </>
  );
}

export default ScheduledMessagesTable;
